/**
 * Top navigation menu bar for DEX layout.
 * Layout: logo (left) | 6 menu buttons (center, collapsible on mobile) | config + connect (right).
 * Height: 58px. Responsive: menu items collapse into hamburger layout on mobile (≤768px).
 */
import React, { useState } from "react";
import "./MenuBar.css";
import WalletModal from "./WalletModal";

export interface MenuBarProps {
  /** Brand name shown next to logo */
  brandName?: string;
  /** Menu items (label, optional href, optional hasDropdown) */
  menuItems?: Array<{ label: string; href?: string; hasDropdown?: boolean }>;
  /** Wallet address when connected; empty shows "Connect Wallet" */
  walletAddress?: string;
  /** Called when Connect/Connect Wallet button is clicked (when not connected) */
  onConnectClick?: () => void;
  /** Display name for the connected RPC network chain (e.g. "Morpheum Mainnet") */
  networkChainName?: string;
  /** Called when Bucket Transfer is clicked in the wallet modal */
  onBucketTransfer?: () => void;
  /** Called when Coin Transfer is clicked in the wallet modal */
  onCoinTransfer?: () => void;
  /** Called when Disconnect is clicked in the wallet modal */
  onDisconnect?: () => void;
  /** Called when settings gear is clicked */
  onSettingsClick?: () => void;
  /** Optional block/status indicator text (e.g. "Block 176475481") */
  statusText?: string;
  /** Optional network/language dropdown click */
  onNetworkClick?: () => void;
  /** Custom logo element; defaults to Morpheum-style placeholder */
  logo?: React.ReactNode;
}

const DEFAULT_MENU_ITEMS = [
  { label: "Trade" },
  { label: "Portfolio" },
  { label: "Public Pools" },
  { label: "Staking" },
  { label: "Stats" },
  { label: "Tools", hasDropdown: true },
];

function truncateAddress(addr: string, chars = 6): string {
  if (addr.length <= chars * 2) return addr;
  return `${addr.slice(0, chars)}...${addr.slice(-chars)}`;
}

function MenuBar({
  brandName = "Morpheum",
  menuItems = DEFAULT_MENU_ITEMS,
  walletAddress = "",
  onConnectClick,
  networkChainName = "Morpheum Network",
  onBucketTransfer,
  onCoinTransfer,
  onDisconnect,
  onSettingsClick,
  statusText,
  onNetworkClick,
  logo,
}: MenuBarProps): JSX.Element {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [walletModalOpen, setWalletModalOpen] = useState(false);

  const isConnected = Boolean(walletAddress?.trim());

  const handleConnectClick = () => {
    if (isConnected) {
      setWalletModalOpen(true);
    } else {
      onConnectClick?.();
    }
  };

  return (
    <header className="menubar" role="banner">
      <div className="menubar-inner">
        {/* Left: Logo + Brand */}
        <div className="menubar-left">
          <a href="/" className="menubar-logo-link" aria-label={`${brandName} home`}>
            {logo ?? (
              <div className="menubar-logo-placeholder" aria-hidden>
                <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M6 4L22 4L22 24L6 24L6 4Z" stroke="currentColor" strokeWidth="2" fill="none" />
                  <path d="M6 14L22 14" stroke="currentColor" strokeWidth="2" />
                </svg>
              </div>
            )}
            <span className="menubar-brand">{brandName}</span>
          </a>
        </div>

        {/* Center: Menu buttons (desktop) / Hamburger (mobile) */}
        <nav className="menubar-nav" aria-label="Main navigation">
          <button
            type="button"
            className="menubar-hamburger"
            aria-expanded={mobileMenuOpen}
            aria-controls="menubar-menu"
            onClick={() => setMobileMenuOpen((o) => !o)}
          >
            <span />
            <span />
            <span />
          </button>
          <ul
            id="menubar-menu"
            className={`menubar-menu ${mobileMenuOpen ? "menubar-menu-open" : ""}`}
          >
            {menuItems.map((item) => (
              <li key={item.label}>
                <a
                  href={item.href ?? "#"}
                  className="menubar-menu-item"
                  onClick={(e) => {
                    if (!item.href || item.href === "#") {
                      e.preventDefault();
                    }
                    setMobileMenuOpen(false);
                  }}
                >
                  {item.label}
                  {item.hasDropdown && (
                    <svg className="menubar-chevron" width="10" height="6" viewBox="0 0 10 6" fill="none" aria-hidden>
                      <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                  )}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        {/* Right: Status, network, settings, connect */}
        <div className="menubar-right">
          {statusText && (
            <div className="menubar-status" title={statusText}>
              <span className="menubar-status-dot" />
              <span className="menubar-status-text">{statusText}</span>
            </div>
          )}
          {onNetworkClick && (
            <button
              type="button"
              className="menubar-icon-btn"
              onClick={onNetworkClick}
              aria-label="Network or language"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
              </svg>
              <svg className="menubar-chevron" width="10" height="6" viewBox="0 0 10 6" fill="none">
                <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
          )}
          <button
            type="button"
            className="menubar-icon-btn"
            onClick={onSettingsClick}
            aria-label="Settings"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="3" />
              <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
            </svg>
          </button>
          <button
            type="button"
            className="menubar-connect-btn"
            onClick={handleConnectClick}
          >
            {isConnected ? truncateAddress(walletAddress) : "Connect Wallet"}
          </button>
        </div>
      </div>
      <WalletModal
        isOpen={walletModalOpen}
        onClose={() => setWalletModalOpen(false)}
        networkChainName={networkChainName}
        walletAddress={walletAddress}
        onBucketTransfer={() => {
          setWalletModalOpen(false);
          onBucketTransfer?.();
        }}
        onCoinTransfer={() => {
          setWalletModalOpen(false);
          onCoinTransfer?.();
        }}
        onDisconnect={onDisconnect}
      />
    </header>
  );
}

export default MenuBar;
